import React, { useEffect, useState } from 'react';
import BossDialog from '../../components/BossDialog';
import { EditPageHeader } from '../../components/EditPageHeader';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useTranslation } from '../../locale/useTranslation';
import * as API from "../../framework/API/api";
import { utility } from '../../framework/utility/utilityProvider';
import { DAI_INPUT_CLASS, DAI_INPUTS_TYPE, ENTITYNAME, LOGEVENT, MODULE, TOAST_TYPES } from '../../framework/constant/constant';
import ToastNotification from '../../components/ToastNotification';

const DaiInputForm = ({ item = {}, cancelEdit, refresh, setData }) => {

  const lang = useTranslation();

  const blankDataItem = {
    ...item,
    Name: item?.copy ? "Copy of " + item?.Name : item?.Name ?? "",
    Type: item?.Type ? { _id: item.Type, Description: item.Type } : {},
    InputClass: item?.InputClass ? { _id: item?.InputClass, Description: item?.InputClass } : {},
    Archive: item?.Archive ?? false,
  };

  const [dataItem, setDataItem] = useState(blankDataItem);
  const [sources, setSources] = useState([
    {
      Url: item?.Sources?.[0]?.Url ?? "",
      Username: item?.Sources?.[0]?.Username ?? "",
      PasswordParam: item?.Sources?.[0]?.PasswordParam ?? ""
    },
    {
      Url: item?.Sources?.[1]?.Url ?? "",
      Username: item?.Sources?.[1]?.Username ?? "",
      PasswordParam: item?.Sources?.[1]?.PasswordParam ?? ""
    }
  ]);

  const [types, setTypes] = useState([]);
  const [inputClasses, setInputClasses] = useState([]);

  useEffect(() => {
    loadCombo();
  }, []);

  const loadCombo = async () => {
    const typeRes = Object.keys(DAI_INPUTS_TYPE).map((x) => ({
      _id: DAI_INPUTS_TYPE[x],
      Description: x
    }));
    setTypes(typeRes);

    const inputClassRes = Object.keys(DAI_INPUT_CLASS).map((x) => ({
      _id: DAI_INPUT_CLASS[x],
      Description: x
    }));
    setInputClasses(inputClassRes);

    if (!item?._id) {
      setDataItem(prev => ({
        ...prev,
        Type: utility.getDefaultItem(typeRes),
        InputClass: utility.getDefaultItem(inputClassRes)
      }));
    }
  };

  const onChange = (e) => {
    if (e.target.name === 'Archive') {
      setDataItem({ ...dataItem, Archive: !dataItem.Archive });
    } else if (e.target.name === 'Type' || e.target.name === 'InputClass') {
      setDataItem({ ...dataItem, [e.target.name]: e.value });
    } else {
      setDataItem({ ...dataItem, [e.target.name]: e.target.value });
    }
  };

  const onSourceChange = (index, field, value) => {
    const updatedSources = [...sources];
    updatedSources[index][field] = value;
    setSources(updatedSources);
  };

  const isValid = () => {
    if (!dataItem.Name) {
      ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_enter_name_error_message });
      return false;
    }

    const inputClassId = dataItem.InputClass?._id;

    if (inputClassId === DAI_INPUT_CLASS.SINGLE_INPUT) {
      if (!sources[0].Url.trim()) {
        ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "URL is mandatory" });
        return false;
      }
    } else {
      const hasEmpty = sources.slice(0, 2).some(src => !src.Url.trim());
      if (hasEmpty) {
        ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "All URLs are mandatory" });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    const isSingle = dataItem.InputClass?._id === DAI_INPUT_CLASS.SINGLE_INPUT;

    const saveData = {
      _id: dataItem?._id,
      SID: dataItem?.SID,
      Name: dataItem?.Name,
      Archive: dataItem?.Archive ?? false,
      Type: dataItem?.Type?.Description,
      InputClass: dataItem?.InputClass?.Description,
      Sources: isSingle
        ? [
          {
            Url: sources[0].Url ? sources[0].Url?.trim() : "",
            Username: sources[0].Username || "",
            PasswordParam: sources[0].PasswordParam || ""
          }
        ]
        : sources.slice(0, 2).map((src) => ({
          Url: src.Url ? src.Url?.trim() : "",
          Username: src.Username || "",
          PasswordParam: src.PasswordParam || ""
        })),
      checkDuplicate: true,
      query: [["Name", "=", dataItem.Name]]
    };

    const res = await API.saveData(ENTITYNAME.DAI_INPUTS, saveData);
    if (res.success) {
      const logData = { event: res.message === "updated" ? LOGEVENT.UPDATE : LOGEVENT.INSERT, module: MODULE.DAI_INPUTS, data: res.data, message: res.message};
      API.SaveLogs(logData);
      utility.deleteLocalStorageItem(ENTITYNAME.DAI_INPUTS);
      cancelEdit();
      refresh && refresh();
      setData && setData(res.data)
    } else {
      ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
    }
  };

  const renderSourceFields = (index, title) => (
    <>
      <div className="fw-bold mt-3 mb-2">{title}</div>
      <div className="border rounded p-3">
        <div className="row">
          <div className="col-12">
            <label>{lang.url_label} *</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={sources[index].Url}
              onChange={(e) => onSourceChange(index, "Url", e.target.value)}
            />
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-6">
            <label>{lang.username_label}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={sources[index].Username}
              onChange={(e) => onSourceChange(index, "Username", e.target.value)}
            />
          </div>
          <div className="col-6">
            <label>{lang.password_param_label}</label>
            <input
              type="password"
              className="form-control form-control-sm"
              value={sources[index].PasswordParam}
              onChange={(e) => onSourceChange(index, "PasswordParam", e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <BossDialog title={item?.Name ?? lang.dai_input_dialog_header} onClose={cancelEdit} width="700px">
      <EditPageHeader title="" onSubmit={handleSubmit} onCancel={cancelEdit} showTitle={false} />

      <div className="row mt-2">
        <div className="col-6">
          <label>{lang.name_label} *</label>
          <input
            name="Name"
            type="text"
            className="form-control form-control-sm"
            value={dataItem.Name}
            onChange={onChange}
          />
        </div>
        <div className="col-6">
          <label>{lang.type_label}</label>
          <DropDownList
            className="form-control form-control-sm"
            name='Type'
            data={types}
            textField="Description"
            dataItemKey="_id"
            value={dataItem.Type}
            onChange={onChange}
          />
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-6">
          <label>{lang.input_class_label}</label>
          <DropDownList
            className="form-control form-control-sm"
            name='InputClass'
            data={inputClasses}
            textField="Description"
            dataItemKey="_id"
            value={dataItem.InputClass}
            onChange={onChange}
          />
        </div>
        <div className="col-6 d-flex align-items-end">
          <div>
            <input
              name="Archive"
              type="checkbox"
              className="mr-2"
              checked={dataItem.Archive}
              onChange={onChange}
            />
            <label>{lang.archive}</label>
          </div>
        </div>
      </div>

      {dataItem.InputClass?._id === DAI_INPUT_CLASS.SINGLE_INPUT
        ? renderSourceFields(0, lang.source_label)
        : (
          <>
            {renderSourceFields(0, lang.source_1_label)}
            {renderSourceFields(1, lang.source_2_label)}
          </>
        )}
    </BossDialog>
  );
};

export default DaiInputForm;
